import supabase from './supabaseClient';

/**
 * Resources API utility for interacting with resources endpoints
 * Modified to use Supabase client directly to bypass serverless function issues
 */
const resourcesApi = {
  /**
   * Get paginated resources with optional filtering
   * @param {Object} options - Query options
   * @param {Number} options.page - Page number (default: 1)
   * @param {Number} options.limit - Results per page (default: 12)
   * @param {String} options.category - Filter by category slug
   * @param {String} options.search - Search term for title/content
   * @param {String} options.tag - Filter by tag name
   * @param {Boolean} options.includeUnpublished - Include unpublished resources (admin only)
   * @param {Boolean} options.publishedOnly - Only include published resources
   * @param {Boolean} options.draftsOnly - Only include draft resources
   * @returns {Promise<Object>} - Paginated results
   */
  getResources: async (options = {}) => {
    const { 
      page = 1, 
      limit = 12, 
      category,
      // These variables are currently unused but we'll implement them later
      // Commenting them out to fix ESLint errors
      // search, 
      // tag,
      includeUnpublished = false,
      publishedOnly = false,
      draftsOnly = false
    } = options;
    
    try {
      console.log('Fetching resources directly from Supabase...');
      
      const offset = (page - 1) * limit;
      
      // Build the query - now including resource_categories relationship
      let query = supabase
        .from('resources')
        .select(`
          *,
          resource_categories (id, name, slug)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      // Filter by published status
      if (!includeUnpublished) {
        query = query.eq('is_published', true);
      }
      
      if (publishedOnly) {
        query = query.eq('is_published', true);
      }
      
      if (draftsOnly) {
        query = query.eq('is_published', false);
      }
      
      // Filter by category if provided
      if (category) {
        // First we need to get the category ID from the slug
        const { data: categoryData } = await supabase
          .from('resource_categories')
          .select('id')
          .eq('slug', category)
          .single();
        
        if (categoryData) {
          query = query.eq('category_id', categoryData.id);
        }
      }
      
      // Filter by search term if provided
      if (options.search) {
        // Search in title and summary using ilike (case-insensitive)
        query = query.or(`title.ilike.%${options.search}%,summary.ilike.%${options.search}%`);
      }
      
      // Filter by tag if provided
      if (options.tag) {
        console.log(`Tag filtering not implemented yet: ${options.tag}`);
        // This would require a join or separate query, will implement later
      }
      
      // Execute the query
      const { data, error, count } = await query;
      
      if (error) throw error;
      
      console.log(`Resources fetched successfully: ${data?.length || 0} items`);
      
      // If we have resources, fetch their tags, likes and comments
      if (data && data.length > 0) {
        // Get all resource IDs
        const resourceIds = data.map(resource => resource.id);
        
        // Fetch tags for all resources at once
        const { data: tagsData, error: tagsError } = await supabase
          .from('resource_resource_tags')
          .select('resource_id, resource_tags(*)')
          .in('resource_id', resourceIds);
          
        // Create a map to store tags for each resource
        const resourceTags = {};
        
        if (!tagsError && tagsData) {
          // Group tags by resource_id
          tagsData.forEach(item => {
            if (!resourceTags[item.resource_id]) {
              resourceTags[item.resource_id] = [];
            }
            resourceTags[item.resource_id].push(item.resource_tags);
          });
        }
        
        // Fetch like counts for all resources
        const likeCounts = {};
        for (const resourceId of resourceIds) {
          const { count: likeCount, error: likeError } = await supabase
            .from('resource_likes')
            .select('id', { count: 'exact' })
            .eq('resource_id', resourceId);
            
          if (!likeError) {
            likeCounts[resourceId] = likeCount || 0;
          }
        }
        
        // Fetch comment counts for all resources
        const commentCounts = {};
        for (const resourceId of resourceIds) {
          const { count: commentCount, error: commentError } = await supabase
            .from('resource_comments')
            .select('id', { count: 'exact' })
            .eq('resource_id', resourceId);
            
          if (!commentError) {
            commentCounts[resourceId] = commentCount || 0;
          }
        }
        
        // Add tags, like counts, comment counts, and extract category name to each resource
        data.forEach(resource => {
          resource.tags = resourceTags[resource.id] || [];
          resource.like_count = likeCounts[resource.id] || 0;
          resource.comments = [];
          resource.comment_count = commentCounts[resource.id] || 0;
          // Make sure view_count is always available
          resource.view_count = resource.view_count || 0;
          
          // Extract category name from the resource_categories relationship
          if (resource.resource_categories) {
            resource.category_name = resource.resource_categories.name;
          }
        });
      }
      
      return {
        success: true,
        data: data || [],
        total: count || 0,
        page: parseInt(page),
        limit: parseInt(limit)
      };
    } catch (error) {
      console.error('Error fetching resources:', error);
      // Return empty result instead of throwing
      return {
        success: true,
        data: [],
        total: 0,
        page: parseInt(page),
        limit: parseInt(limit)
      };
    }
  },
  
  /**
   * Get the most recent resources
   * @param {Number} limit - Number of resources to fetch (default: 3)
   * @returns {Promise<Array>} - Array of recent resources
   */
  getRecentResources: async (limit = 3) => {
    try {
      console.log(`Fetching ${limit} recent resources...`);
      
      // Build the query
      let query = supabase
        .from('resources')
        .select(`
          *,
          resource_categories (id, name, slug)
        `)
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      // Execute the query
      const { data, error } = await query;
      
      if (error) throw error;
      
      console.log(`Recent resources fetched successfully: ${data?.length || 0} items`);
      
      // If we have resources, fetch their tags, likes and comments
      if (data && data.length > 0) {
        // Get all resource IDs
        const resourceIds = data.map(resource => resource.id);
        
        // Fetch like counts for all resources
        const likeCounts = {};
        for (const resourceId of resourceIds) {
          const { count: likeCount, error: likeError } = await supabase
            .from('resource_likes')
            .select('id', { count: 'exact' })
            .eq('resource_id', resourceId);
            
          if (!likeError) {
            likeCounts[resourceId] = likeCount || 0;
          }
        }
        
        // Fetch comment counts for all resources
        const commentCounts = {};
        for (const resourceId of resourceIds) {
          const { count: commentCount, error: commentError } = await supabase
            .from('resource_comments')
            .select('id', { count: 'exact' })
            .eq('resource_id', resourceId);
            
          if (!commentError) {
            commentCounts[resourceId] = commentCount || 0;
          }
        }
        
        // Add like counts, comment counts, and extract category name to each resource
        data.forEach(resource => {
          resource.like_count = likeCounts[resource.id] || 0;
          resource.comment_count = commentCounts[resource.id] || 0;
          // Make sure view_count is always available
          resource.view_count = resource.view_count || 0;
          
          // Extract category name from the resource_categories relationship
          if (resource.resource_categories) {
            resource.category_name = resource.resource_categories.name;
          }
        });
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching recent resources:', error);
      return [];
    }
  },
  
  /**
   * Get a single resource by slug
   * @param {String} slug - Resource slug
   * @returns {Promise<Object>} - Resource data
   */
  getResourceBySlug: async (slug) => {
    try {
      console.log(`Fetching resource by slug: ${slug}`);
      
      // First, get just the resource and its basic relationships
      const { data, error } = await supabase
        .from('resources')
        .select(`
          *,
          resource_categories (*)
        `)
        .eq('slug', slug)
        .single();
      
      if (error) throw error;
      
      // Increment view count
      const newViewCount = (data.view_count || 0) + 1;
      
      // Update the view count in the database
      const { error: updateError } = await supabase
        .from('resources')
        .update({ view_count: newViewCount })
        .eq('id', data.id);
        
      if (updateError) {
        console.error('Error incrementing view count:', updateError);
        // Continue anyway - viewing should still work even if count update fails
      }
      
      // Then fetch tags separately
      const { data: tagsData, error: tagsError } = await supabase
        .from('resource_resource_tags')
        .select(`
          resource_tags (*)
        `)
        .eq('resource_id', data.id);
      
      // Fetch comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('resource_comments')
        .select('*')
        .eq('resource_id', data.id)
        .order('created_at', { ascending: false });
      
      // Fetch user profiles for comments if we have any comments
      if (commentsData && commentsData.length > 0 && !commentsError) {
        // Get unique user IDs from comments
        const userIds = [...new Set(commentsData.map(comment => comment.user_id))];
        
        // Fetch user profiles
        const { data: userProfiles, error: userProfilesError } = await supabase
          .from('user_profiles')
          .select('user_id, name, photo_url')
          .in('user_id', userIds);
          
        // Join user profiles with comments
        if (!userProfilesError && userProfiles) {
          // Create a map of user profiles by user_id for quick lookup
          const userProfilesMap = {};
          userProfiles.forEach(profile => {
            userProfilesMap[profile.user_id] = profile;
          });
          
          // Add user profile to each comment
          commentsData.forEach(comment => {
            comment.user_profiles = userProfilesMap[comment.user_id] || null;
          });
        }
      }
      
      // Check if current user has liked this resource
      let isLikedByUser = false;
      let likeCount = 0;
      
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Check if user has liked this resource
          const { data: likeData } = await supabase
            .from('resource_likes')
            .select('id')
            .eq('resource_id', data.id)
            .eq('user_id', user.id)
            .maybeSingle();
            
          isLikedByUser = !!likeData;
        }
        
        // Get total like count
        const { count, error: countError } = await supabase
          .from('resource_likes')
          .select('id', { count: 'exact' })
          .eq('resource_id', data.id);
          
        if (!countError) {
          likeCount = count || 0;
        }
      } catch (userError) {
        console.error('Error checking like status:', userError);
      }
      
      // Process resource data to match expected format in UI
      const processedData = {
        ...data,
        // Format tags into the expected structure
        tags: tagsError ? [] : tagsData.map(item => item.resource_tags),
        // Add comments
        comments: commentsError ? [] : commentsData,
        // Add like info
        view_count: newViewCount,
        like_count: likeCount,
        is_liked_by_user: isLikedByUser
      };
      
      return processedData;
    } catch (error) {
      console.error('Error fetching resource:', error);
      throw error;
    }
  },
  
  /**
   * Toggle like status for a resource
   * @param {String} id - Resource ID
   * @returns {Promise<Object>} - Updated like status
   */
  toggleLike: async (id) => {
    try {
      console.log('Toggling like directly with Supabase...');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Authentication required');
      }
      
      // Check if the user has already liked this resource
      const { data: existingLike, error: checkError } = await supabase
        .from('resource_likes')
        .select('id')
        .eq('resource_id', id)
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (checkError) throw checkError;
      
      let liked = false;
      
      if (existingLike) {
        // Unlike: Remove the like
        const { error: unlikeError } = await supabase
          .from('resource_likes')
          .delete()
          .eq('id', existingLike.id);
          
        if (unlikeError) throw unlikeError;
        
        liked = false;
      } else {
        // Like: Add a new like
        const { error: likeError } = await supabase
          .from('resource_likes')
          .insert({
            resource_id: id,
            user_id: user.id
          });
          
        if (likeError) throw likeError;
        
        liked = true;
      }
      
      // Get updated like count
      const { count, error: countError } = await supabase
        .from('resource_likes')
        .select('id', { count: 'exact' })
        .eq('resource_id', id);
        
      if (countError) throw countError;
      
      return {
        liked: liked,
        like_count: count || 0
      };
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    }
  },
  
  /**
   * Add a comment to a resource
   * @param {String} id - Resource ID
   * @param {String} comment - Comment text
   * @returns {Promise<Object>} - Created comment
   */
  addComment: async (id, comment) => {
    try {
      console.log('Adding comment directly with Supabase...');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Authentication required');
      }
      
      // Insert the comment
      const { data, error } = await supabase
        .from('resource_comments')
        .insert({
          resource_id: id,
          user_id: user.id,
          comment: comment,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Fetch the user profile for this comment
      const { data: userProfileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, name, photo_url')
        .eq('user_id', user.id)
        .single();
        
      // Add user profile data to the comment object
      const enhancedComment = {
        ...data,
        user_profiles: profileError ? null : userProfileData
      };
      
      return enhancedComment;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  },
  
  /**
   * Get all resource categories
   * @returns {Promise<Array>} - Categories
   */
  getCategories: async () => {
    try {
      console.log('Fetching categories directly from Supabase...');
      
      const { data, error } = await supabase
        .from('resource_categories')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      console.log(`Categories fetched successfully: ${data?.length || 0} items`);
      
      return data || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Return empty array instead of throwing to avoid crashing UI
      return [];
    }
  },
  
  /**
   * Get all resource tags
   * @returns {Promise<Array>} - Tags
   */
  getTags: async () => {
    try {
      const response = await fetch('/api/resources/tags', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch tags');
      }
      
      return data.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  },
  
  /**
   * Create a new resource (admin only)
   * @param {Object} resourceData - Resource data
   * @param {Array} tags - Array of tag strings
   * @returns {Promise<Object>} - Created resource
   */
  createResource: async (resourceData, tags = []) => {
    try {
      console.log('Creating resource directly in Supabase...');
      
      // Remove tags from resourceData to prevent the error
      const { tags: removedTags, ...resourceDataWithoutTags } = resourceData;
      
      // Create the resource first
      const { data, error } = await supabase
        .from('resources')
        .insert([resourceDataWithoutTags])
        .select()
        .single();
      
      if (error) throw error;
      
      // If we have tags, add them to the resource using the junction table
      if (tags && tags.length > 0) {
        // First, ensure all tags exist in the resource_tags table
        for (const tagName of tags) {
          // Check if tag exists - use filter syntax compatible with PostgreSQL
          let { data: existingTag, error: queryError } = await supabase
            .from('resource_tags')
            .select('id')
            .eq('name', tagName)
            .maybeSingle();
            
          if (queryError) {
            console.error('Error checking tag existence:', queryError);
            continue;
          }
            
          let tagId;
          
          if (!existingTag) {
            // Create the tag if it doesn't exist
            const { data: newTag, error: tagError } = await supabase
              .from('resource_tags')
              .insert({ name: tagName })
              .select()
              .single();
              
            if (tagError) {
              console.error('Error creating tag:', tagError);
              continue;
            }
            tagId = newTag.id;
          } else {
            tagId = existingTag.id;
          }
          
          // Link the tag to the resource
          const { error: linkError } = await supabase
            .from('resource_resource_tags')
            .insert({
              resource_id: data.id,
              tag_id: tagId
            });
            
          if (linkError) {
            console.error('Error linking tag to resource:', linkError);
          }
        }
      }
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error creating resource:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing resource (admin only)
   * @param {string} id - Resource ID
   * @param {Object} resourceData - Updated resource data
   * @param {Array} tags - Array of tag strings
   * @returns {Promise<Object>} - Updated resource
   */
  updateResource: async (id, resourceData, tags = []) => {
    try {
      console.log('Updating resource directly in Supabase...');
      
      // Remove tags from resourceData to prevent the error
      const { tags: removedTags, ...resourceDataWithoutTags } = resourceData;
      
      // Update the resource
      const { data, error } = await supabase
        .from('resources')
        .update(resourceDataWithoutTags)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Handle tags for the updated resource
      if (tags) {
        // First, remove all existing tag associations
        const { error: deleteError } = await supabase
          .from('resource_resource_tags')
          .delete()
          .eq('resource_id', id);
          
        if (deleteError) {
          console.error('Error removing existing tag associations:', deleteError);
        } else {
          // Then add the new tags
          if (tags.length > 0) {
            // Ensure all tags exist in the resource_tags table
            for (const tagName of tags) {
              // Check if tag exists
              let { data: existingTag, error: queryError } = await supabase
                .from('resource_tags')
                .select('id')
                .eq('name', tagName)
                .maybeSingle();
                
              if (queryError) {
                console.error('Error checking tag existence:', queryError);
                continue;
              }
                
              let tagId;
              
              if (!existingTag) {
                // Create the tag if it doesn't exist
                const { data: newTag, error: tagError } = await supabase
                  .from('resource_tags')
                  .insert({ name: tagName })
                  .select()
                  .single();
                  
                if (tagError) {
                  console.error('Error creating tag:', tagError);
                  continue;
                }
                tagId = newTag.id;
              } else {
                tagId = existingTag.id;
              }
              
              // Link the tag to the resource
              const { error: linkError } = await supabase
                .from('resource_resource_tags')
                .insert({
                  resource_id: id,
                  tag_id: tagId
                });
                
              if (linkError) {
                console.error('Error linking tag to resource:', linkError);
              }
            }
          }
        }
      }
      
      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Error updating resource:', error);
      throw error;
    }
  },
  
  /**
   * Delete a resource (admin only)
   * @param {String} id - Resource ID
   * @returns {Promise<Object>} - Success message
   */
  deleteResource: async (id) => {
    try {
      console.log(`Deleting resource ${id} directly from Supabase...`);
      
      const { error } = await supabase
        .from('resources')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return {
        success: true,
        message: 'Resource deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting resource:', error);
      throw error;
    }
  }
};

export default resourcesApi; 